import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, ArrowLeft, Info, Pencil, Plus, Trash2 } from 'lucide-react'
import { listAccounts } from '@/api/accounts'
import { listCategories } from '@/api/categories'
import {
  archiveContact,
  deleteContact,
  deleteOpeningEntry,
  deleteSettlement,
  listContactEntries,
  listContactSettlements,
  listContacts,
  unarchiveContact,
  type OwedEntry,
  type SettlementSummary,
} from '@/api/people'
import { Button } from '@/components/ui/button'
import { useFormatCurrency, useListAmountFormat } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { ROUTES } from '@/routes/routes'
import { ALL_TIME, transactionsLink } from '@/lib/transactionsLink'
import { AddTransactionModal } from '@/components/transactions/TransactionForms'
import { SettleUpModal } from '@/components/owed/SettleUpModal'
import { CashEntryModal } from '@/components/owed/CashEntryModal'
import { listPersonalLoans, type LoanDirection } from '@/api/personalLoans'
import { PersonalLoanFormModal } from '@/components/personalLoans/PersonalLoanFormModal'
import { PersonalLoanRow } from '@/components/personalLoans/PersonalLoanRow'
import { PERSONAL_LOANS_ENABLED } from '@/lib/featureFlags'
import { ConfirmDialog } from '@/components/personalLoans/ConfirmDialog'
import { PersonHero } from '@/components/people/PersonHero'
import { OpenItems } from '@/components/people/OpenItems'
import { PEOPLE_CARD, PEOPLE_SECTION_LABEL } from '@/components/people/peopleUi'
import { PersonHistory } from '@/components/people/PersonHistory'
import { AddWithPersonSheet, type AddChoice } from '@/components/people/AddWithPersonSheet'
import { PersonActionsMenu, type PersonAction } from '@/components/people/PersonActionsMenu'
import { RenamePersonDialog } from '@/components/people/RenamePersonDialog'
import { buildHistory, netOf } from '@/components/people/peopleStats'
import { cn } from '@/lib/utils'
import { ABOVE_TAB_BAR } from '@/components/layout/navItems'

type ApiError = { response?: { data?: { detail?: string } } }
const detailOf = (err: unknown) => (err as ApiError)?.response?.data?.detail

function UndoSettlementDialog({
  settlement,
  onConfirm,
  onCancel,
  isPending,
  errorMessage,
}: {
  settlement: SettlementSummary
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
  errorMessage?: string
}) {
  const { format } = useFormatCurrency()
  return (
    <ConfirmDialog
      title="Undo settlement"
      confirmLabel="Undo"
      pendingLabel="Undoing…"
      onConfirm={onConfirm}
      onCancel={onCancel}
      isPending={isPending}
      errorMessage={errorMessage}
    >
      Undo this settlement? The entries it paid off become outstanding again.
      {settlement.netAmount > 0 &&
        ` The ${format(settlement.netAmount)} payment is removed and the account balance reverts.`}
    </ConfirmDialog>
  )
}

export default function PersonDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isReadOnly = useIsReadOnly()
  const format = useListAmountFormat()
  const queryClient = useQueryClient()
  const addTriggerRef = useRef<HTMLButtonElement>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [settling, setSettling] = useState(false)
  const [adding, setAdding] = useState<{ includeYou: boolean; paidByThem?: boolean } | null>(null)
  const [cash, setCash] = useState<'LENT' | 'BORROWED' | null>(null)
  const [newLoan, setNewLoan] = useState<LoanDirection | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [undoing, setUndoing] = useState<SettlementSummary | null>(null)
  const [removingOpening, setRemovingOpening] = useState<OwedEntry | null>(null)

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', { includeArchived: true }],
    queryFn: () => listContacts(true),
  })
  const contact = contacts.find((c) => c.id === id)

  const { data: entries = [], isSuccess: entriesLoaded } = useQuery({
    queryKey: ['contacts', id, 'entries', 'all'],
    queryFn: () => listContactEntries(id, 'all'),
    enabled: !!id,
  })
  const { data: settlements = [], isSuccess: settlementsLoaded } = useQuery({
    queryKey: ['contacts', id, 'settlements'],
    queryFn: () => listContactSettlements(id),
    enabled: !!id,
  })
  const { data: loans = [], isSuccess: loansLoaded } = useQuery({
    queryKey: ['personal-loans', { contactId: id }],
    queryFn: () => listPersonalLoans(id),
    enabled: !!id && PERSONAL_LOANS_ENABLED,
  })
  const { data: accountsList } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => listAccounts(),
    enabled: adding !== null,
  })
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
    enabled: adding !== null,
  })

  const invalidate = (...keys: string[]) =>
    keys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

  const undoMutation = useMutation({
    mutationFn: (s: SettlementSummary) => deleteSettlement(s.id),
    onSuccess: () => {
      invalidate('contacts', 'owed-summary', 'transactions', 'accounts', 'dashboard', 'budget')
      setUndoing(null)
    },
  })
  const removeOpening = useMutation({
    mutationFn: (e: OwedEntry) => deleteOpeningEntry(e.id),
    onSuccess: () => {
      invalidate('contacts', 'owed-summary', 'accounts', 'dashboard')
      setRemovingOpening(null)
    },
  })
  const archiveMutation = useMutation({
    mutationFn: () => archiveContact(id),
    onSuccess: () => invalidate('contacts'),
  })
  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveContact(id),
    onSuccess: () => invalidate('contacts'),
  })
  const deleteMutation = useMutation({
    mutationFn: () => deleteContact(id),
    onSuccess: () => {
      invalidate('contacts')
      navigate(ROUTES.APP.PEOPLE)
    },
  })

  const byNewest = (a: OwedEntry, b: OwedEntry) => b.entryDate.localeCompare(a.entryDate)
  const openEntries = entries.filter((e) => e.outstanding > 0).sort(byNewest)
  const history = buildHistory(entries, settlements)
  const name = contact?.name ?? ''
  const canWrite = !isReadOnly && !!contact && !contact.archived
  const net = contact ? netOf(contact) : 0
  const hasLeftColumn = openEntries.length > 0 || loans.length > 0

  function pick(choice: AddChoice) {
    setSheetOpen(false)
    if (choice === 'SPLIT') setAdding({ includeYou: true })
    else if (choice === 'PAID_FOR_THEM') setAdding({ includeYou: false })
    else if (choice === 'THEY_PAID') setAdding({ includeYou: false, paidByThem: true })
    else if (choice === 'LENT' || choice === 'BORROWED') setCash(choice)
    else setNewLoan(choice === 'GIVE_LOAN' ? 'LENT' : 'BORROWED')
  }

  const actions: PersonAction[] = contact
    ? [
        { label: 'Rename', icon: Pencil, onSelect: () => setRenaming(true) },
        ...(!contact.archived
          ? [
              {
                label: 'Archive',
                icon: Archive,
                onSelect: () => archiveMutation.mutate(),
                disabledReason:
                  contact.owedToUs !== 0 || contact.weOwe !== 0
                    ? 'Settle up first'
                    : loans.some((l) => !l.closed)
                      ? 'Close their loans first'
                      : undefined,
              },
            ]
          : []),
        {
          label: 'Delete',
          icon: Trash2,
          destructive: true,
          onSelect: () => {
            deleteMutation.reset()
            setDeleting(true)
          },
          disabledReason: !(
            entriesLoaded &&
            settlementsLoaded &&
            (loansLoaded || !PERSONAL_LOANS_ENABLED)
          )
            ? 'Checking their history…'
            : entries.length > 0 || settlements.length > 0 || loans.length > 0
              ? 'Has history — archive instead'
              : undefined,
        },
      ]
    : []

  const actionError = detailOf(archiveMutation.error) ?? detailOf(unarchiveMutation.error)

  return (
    <div className={cn('flex flex-col gap-5 p-4 sm:p-6 md:p-10', canWrite && 'pb-28 md:pb-10')}>
      <div className="-my-2 flex items-center justify-between">
        <Link
          to={ROUTES.APP.PEOPLE}
          className="flex h-11 items-center gap-1 text-sm text-[var(--fl-text-2)] hover:text-[var(--fl-heading)]"
        >
          <ArrowLeft size={14} /> People
        </Link>
        <div className="flex items-center gap-2">
          {contact && (
            <Link
              to={transactionsLink(
                { ...ALL_TIME, contactId: contact.id },
                { back: ROUTES.APP.PERSON_DETAIL(contact.id) }
              )}
              className="text-sm font-medium text-[var(--fl-primary)] hover:underline"
            >
              Transactions →
            </Link>
          )}
          {!isReadOnly && contact && <PersonActionsMenu name={name} actions={actions} />}
        </div>
      </div>

      {contact?.archived && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-[var(--fl-warning-bg)] px-4 py-3 text-[13px] text-[var(--fl-warning-dark)]">
          <Archive size={18} aria-hidden="true" />
          <span className="min-w-0 flex-1">
            Archived. Hidden from pickers when you add a split or a loan.
          </span>
          {!isReadOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => unarchiveMutation.mutate()}
              disabled={unarchiveMutation.isPending}
              className="border-[var(--fl-warning-mid)] bg-transparent font-semibold text-[var(--fl-warning-dark)]"
            >
              Unarchive
            </Button>
          )}
        </div>
      )}
      {actionError && <p className="text-sm text-[var(--fl-error)]">{actionError}</p>}

      {contact && (
        <PersonHero
          contact={contact}
          actions={
            canWrite && (
              // Phone: pinned just above the tab bar, within thumb reach.
              <div
                className={`${ABOVE_TAB_BAR} fixed inset-x-0 z-30 flex gap-2.5 border-t border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 py-3 md:static md:z-auto md:w-56 md:shrink-0 md:flex-col-reverse md:border-0 md:bg-transparent md:p-0`}
              >
                <Button
                  ref={addTriggerRef}
                  variant="outline"
                  onClick={() => setSheetOpen(true)}
                  className="h-12 flex-1 gap-1.5 rounded-xl border-[var(--fl-border)] bg-[var(--fl-surface)] font-medium text-[var(--fl-text-1)] md:h-11 md:flex-none"
                >
                  <Plus size={18} /> Add
                </Button>
                {openEntries.length > 0 && (
                  <Button
                    onClick={() => setSettling(true)}
                    className="h-12 flex-[2] rounded-xl font-semibold md:h-11 md:flex-none"
                  >
                    Settle up{net !== 0 && ` · ${format(Math.abs(net))}`}
                  </Button>
                )}
              </div>
            )
          }
        />
      )}

      <div
        className={cn(
          'grid items-start gap-5',
          hasLeftColumn && history.length > 0 && 'md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]'
        )}
      >
        {hasLeftColumn && (
          <div className="flex flex-col gap-5">
            {openEntries.length > 0 && (
              <OpenItems
                contactId={id}
                entries={openEntries}
                name={name}
                onRemoveOpening={canWrite ? setRemovingOpening : undefined}
              />
            )}
            {loans.length > 0 && (
              <section aria-label="Loans">
                <h2 className={PEOPLE_SECTION_LABEL}>Loans · {loans.length}</h2>
                <p className="mb-2 flex items-start gap-2.5 rounded-xl bg-[var(--fl-surface-subtle)] px-3.5 py-3 text-[13px] text-[var(--fl-text-2)]">
                  <Info size={16} aria-hidden="true" className="mt-px shrink-0" />
                  Loans aren’t part of Settle up. Open a loan to record a repayment or write it off.
                </p>
                <ul className={cn(PEOPLE_CARD, 'divide-y divide-[var(--fl-divider)]')}>
                  {loans.map((l) => (
                    <PersonalLoanRow key={l.accountId} loan={l} title={l.name} />
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
        {history.length > 0 && (
          <PersonHistory
            name={name}
            months={history}
            onUndo={
              isReadOnly
                ? undefined
                : (s) => {
                    undoMutation.reset()
                    setUndoing(s)
                  }
            }
          />
        )}
      </div>

      {sheetOpen && (
        <AddWithPersonSheet
          name={name}
          showLoans={PERSONAL_LOANS_ENABLED}
          onPick={pick}
          onClose={() => {
            setSheetOpen(false)
            addTriggerRef.current?.focus()
          }}
        />
      )}
      {renaming && contact && (
        <RenamePersonDialog contact={contact} onClose={() => setRenaming(false)} />
      )}
      {deleting && (
        <ConfirmDialog
          title={`Delete ${name}`}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setDeleting(false)}
          isPending={deleteMutation.isPending}
          errorMessage={
            deleteMutation.isError
              ? (detailOf(deleteMutation.error) ?? 'Could not delete. Please try again.')
              : null
          }
        >
          {name} has no history, so they’re removed completely.
        </ConfirmDialog>
      )}
      {undoing && (
        <UndoSettlementDialog
          settlement={undoing}
          onConfirm={() => undoMutation.mutate(undoing)}
          onCancel={() => setUndoing(null)}
          isPending={undoMutation.isPending}
          errorMessage={
            undoMutation.isError
              ? (detailOf(undoMutation.error) ??
                'Could not undo this settlement. Please try again.')
              : undefined
          }
        />
      )}
      {removingOpening && (
        <ConfirmDialog
          title="Remove balance"
          confirmLabel="Remove"
          pendingLabel="Removing…"
          onConfirm={() => removeOpening.mutate(removingOpening)}
          onCancel={() => setRemovingOpening(null)}
          isPending={removeOpening.isPending}
          errorMessage={
            removeOpening.isError
              ? (detailOf(removeOpening.error) ?? 'Could not remove this. Please try again.')
              : null
          }
        >
          Remove the {format(removingOpening.amount)} that was already owed? No account changes.
        </ConfirmDialog>
      )}
      {settling && contact && (
        <SettleUpModal contact={contact} entries={openEntries} onClose={() => setSettling(false)} />
      )}
      {cash && contact && (
        <CashEntryModal contact={contact} direction={cash} onClose={() => setCash(null)} />
      )}
      {newLoan && (
        <PersonalLoanFormModal
          initialDirection={newLoan}
          initialContactId={id}
          onClose={() => setNewLoan(null)}
        />
      )}
      {adding && (
        <AddTransactionModal
          accounts={accountsList?.accounts ?? []}
          categories={categoriesData?.categories ?? []}
          initialSplitContactId={adding.paidByThem ? undefined : id}
          initialPaidByContactId={adding.paidByThem ? id : undefined}
          initialIncludeYou={adding.includeYou}
          onClose={() => setAdding(null)}
        />
      )}
    </div>
  )
}
